import { FormEvent, InputHTMLAttributes, useState } from "react";
import { Plus, RotateCcw, X } from "lucide-react";
import { Modal } from "../Modal";
import { apiFieldErrors } from "../../utils/apiError";
import type { AllowanceLine } from "../../services/staffService";

/** What the form is opened against: a new request, or a returned one to revise. */
export type PayChangeTarget = {
  staffId: number;
  name: string;
  currency: string;
  currentSalary: string | null;
  /** Allowances in force now, to compare against. */
  currentAllowanceTotal: number;
  /** Lines the form starts with: today's, or those on the returned request. */
  startingAllowances: AllowanceLine[];
  positionTitle?: string;
  payRange?: { min: number; max: number };
  requestId?: number;
  proposedSalary?: string;
  effectiveDate?: string;
  reason?: string;
  returnReason?: string;
};

export type PayChangeValues = {
  proposed_salary: string;
  proposed_allowances: AllowanceLine[];
  effective_date: string;
  reason: string;
};

const FORM_ID = "pay-change-form";
const COMMON_ALLOWANCES = ["Housing", "Transport", "Lunch", "Responsibility"];

export const totalOf = (lines: AllowanceLine[]) =>
  lines.reduce((running, line) => running + (Number(line.amount) || 0), 0);

/** "+50,000 (+10%)", or "No change". */
export function describeChange(current: number, proposed: number) {
  const change = proposed - current;
  if (!change) return "No change";
  const sign = change > 0 ? "+" : "−";
  const percent = current > 0 ? ` (${sign}${Math.abs(Math.round((change / current) * 100))}%)` : "";
  return `${sign}${Math.abs(change).toLocaleString()}${percent}`;
}

function AmountInput({
  currency,
  className = "",
  ...props
}: { currency: string; className?: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={`relative ${className}`}>
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">
        {currency}
      </span>
      <input type="number" step="0.01" className="input pl-12 text-right tabular-nums" {...props} />
    </div>
  );
}

export default function PayChangeModal({
  target,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  target: PayChangeTarget;
  pending: boolean;
  error: unknown;
  onSubmit: (values: PayChangeValues) => void;
  onClose: () => void;
}) {
  const [salary, setSalary] = useState(target.proposedSalary ?? target.currentSalary ?? "");
  const [lines, setLines] = useState<AllowanceLine[]>(target.startingAllowances);
  const [effectiveDate, setEffectiveDate] = useState(target.effectiveDate ?? "");
  const [reason, setReason] = useState(target.reason ?? "");
  const { fields: errors, message } = apiFieldErrors(
    error,
    ["proposed_salary", "proposed_allowances", "effective_date", "reason"],
    "The pay change could not be submitted. Please try again.",
  );

  const editLine = (index: number, patch: Partial<AllowanceLine>) =>
    setLines(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  const named = lines.filter((line) => line.name.trim());
  const currentBasic = Number(target.currentSalary ?? 0);
  const proposedBasic = Number(salary) || 0;
  const proposedAllowances = totalOf(named);
  const range = target.payRange;
  const outsideRange =
    range && proposedBasic > 0 && (proposedBasic < range.min || (range.max > 0 && proposedBasic > range.max));
  const unusedCommon = COMMON_ALLOWANCES.filter(
    (name) => !lines.some((line) => line.name.trim().toLowerCase() === name.toLowerCase()),
  );

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit({
      proposed_salary: salary,
      proposed_allowances: named.map((line) => ({ name: line.name.trim(), amount: line.amount })),
      effective_date: effectiveDate,
      reason: reason.trim(),
    });
  };

  const rows: [string, number, number][] = [
    ["Basic pay", currentBasic, proposedBasic],
    ["Allowances", target.currentAllowanceTotal, proposedAllowances],
    ["Gross pay", currentBasic + target.currentAllowanceTotal, proposedBasic + proposedAllowances],
  ];

  return (
    <Modal
      isOpen
      size="2xl"
      title={target.requestId ? "Revise pay change" : "Request pay change"}
      onClose={() => {
        if (!pending) onClose();
      }}
      footer={
        <>
          <button type="button" className="btn btn-outline" disabled={pending} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={FORM_ID} disabled={pending} className="btn btn-primary">
            {pending ? "Submitting…" : target.requestId ? "Resubmit for approval" : "Submit for approval"}
          </button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={submit} className="font-refined space-y-5">
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <p className="font-semibold text-gray-900">{target.name}</p>
          <p className="text-xs text-gray-500">
            {target.positionTitle ? `${target.positionTitle} · ` : ""}
            Goes to the finance approval chain before payroll uses it.
          </p>
        </div>

        {target.requestId && (
          <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
            <RotateCcw className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p>
              <strong className="font-semibold">Returned for changes.</strong>{" "}
              {target.returnReason || "Revise the proposal and resubmit it."}
            </p>
          </div>
        )}

        {message && (
          <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            {message}
          </p>
        )}

        <label className="block text-sm font-medium text-gray-700">
          Proposed basic pay *
          <AmountInput
            currency={target.currency}
            required
            min="1"
            value={salary}
            onChange={(event) => setSalary(event.target.value)}
            className="mt-1 sm:w-64"
          />
          {errors.proposed_salary ? (
            <span role="alert" className="mt-1 block text-xs font-normal text-red-700">
              {errors.proposed_salary}
            </span>
          ) : (
            range && (
              <span
                className={`mt-1 block text-xs font-normal ${outsideRange ? "text-amber-700" : "text-gray-500"}`}
              >
                {outsideRange ? "Outside" : "Within"} the {target.positionTitle || "position"} range of{" "}
                {target.currency} {range.min.toLocaleString()}
                {range.max > 0 ? `–${range.max.toLocaleString()}` : " and above"}.
              </span>
            )
          )}
        </label>

        <section>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Allowances</h3>
            <p className="text-xs text-gray-500">Taxable: added to basic pay for PAYE, NSSF and LST.</p>
          </div>
          <div className="mt-2 overflow-hidden rounded-lg border border-gray-200">
            {lines.map((line, index) => (
              <div key={index} className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 last:border-0">
                <input
                  required
                  value={line.name}
                  onChange={(event) => editLine(index, { name: event.target.value })}
                  placeholder="Allowance name"
                  maxLength={100}
                  aria-label={`Allowance ${index + 1} name`}
                  className="input h-9 flex-1"
                />
                <AmountInput
                  currency={target.currency}
                  required
                  min="0.01"
                  value={line.amount}
                  onChange={(event) => editLine(index, { amount: event.target.value })}
                  aria-label={`Allowance ${index + 1} amount`}
                  className="w-44 [&_input]:h-9"
                />
                <button
                  type="button"
                  aria-label={`Remove ${line.name.trim() || `allowance ${index + 1}`}`}
                  className="sheet-action text-red-600"
                  onClick={() => setLines(lines.filter((_, i) => i !== index))}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            {!lines.length && (
              <p className="px-3 py-2.5 text-sm text-gray-500">No allowances: basic pay only.</p>
            )}
            <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 bg-gray-50 px-3 py-2">
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                onClick={() => setLines([...lines, { name: "", amount: "" }])}
              >
                <Plus className="h-3.5 w-3.5" /> Add allowance
              </button>
              {unusedCommon.length > 0 && <span className="text-xs text-gray-400">or</span>}
              {unusedCommon.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setLines([...lines, { name, amount: "" }])}
                  className="rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-700 hover:border-primary-300 hover:text-primary-700"
                >
                  + {name}
                </button>
              ))}
            </div>
          </div>
          {errors.proposed_allowances && (
            <p role="alert" className="mt-1 text-xs text-red-700">
              {errors.proposed_allowances}
            </p>
          )}
        </section>

        <table className="w-full overflow-hidden rounded-lg text-sm ring-1 ring-gray-200">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-1.5 text-left font-semibold"></th>
              <th className="px-3 py-1.5 text-right font-semibold">Current</th>
              <th className="px-3 py-1.5 text-right font-semibold">Proposed</th>
              <th className="px-3 py-1.5 text-right font-semibold">Change</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {rows.map(([label, current, proposed]) => {
              const isGross = label === "Gross pay";
              return (
                <tr key={label} className={isGross ? "border-t border-gray-200 font-semibold" : ""}>
                  <td className="px-3 py-1.5 text-gray-600">{label}</td>
                  <td className="px-3 py-1.5 text-right text-gray-600">{current.toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-right text-gray-900">{proposed.toLocaleString()}</td>
                  <td
                    className={`px-3 py-1.5 text-right ${proposed > current ? "text-green-700" : proposed < current ? "text-red-700" : "text-gray-400"}`}
                  >
                    {describeChange(current, proposed)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="grid gap-4 sm:grid-cols-[12rem_1fr]">
          <label className="block text-sm font-medium text-gray-700">
            Effective date *
            <input
              required
              type="date"
              value={effectiveDate}
              onChange={(event) => setEffectiveDate(event.target.value)}
              className="input mt-1"
            />
            {errors.effective_date && (
              <span role="alert" className="mt-1 block text-xs font-normal text-red-700">
                {errors.effective_date}
              </span>
            )}
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Reason *
            <textarea
              required
              minLength={5}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="input mt-1 h-auto"
              rows={2}
              placeholder="e.g. Annual increment, promotion to head of department"
            />
            {errors.reason && (
              <span role="alert" className="mt-1 block text-xs font-normal text-red-700">
                {errors.reason}
              </span>
            )}
          </label>
        </div>
      </form>
    </Modal>
  );
}
